import { Component, type ErrorInfo, type ReactNode } from 'react'
import { createLogger } from '../lib/logger'

const log = createLogger('ErrorBoundary')

interface Props {
  children: ReactNode
  /** Optional fallback — defaults to a centered reload prompt. */
  fallback?: ReactNode
  /**
   * Identifies this boundary in logs / Sentry breadcrumbs. Use something
   * specific like "shell:thread" so you can tell which pane crashed.
   */
  name?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error(this.props.name ?? 'unnamed', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex h-full min-h-[200px] items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-zinc-500">Something went wrong</p>
            <p className="mt-1 max-w-xs text-xs text-zinc-400">
              {this.state.error?.message}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="mt-3 rounded-md bg-zinc-900 px-3 py-1.5 text-xs text-white hover:bg-zinc-800"
            >
              Reload
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
